import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { CardCarousel, JANE_DEFINED_ROWS } from '@iheartjane/menu';
import { Flex } from '@iheartjane/reefer';
import { useMemo } from 'react';

import { useDebouncedTrack } from '@jane/business-admin/hooks';
import type { EmbeddedMenuRow, MenuRow } from '@jane/business-admin/types';
import { EventNames } from '@jane/business-admin/util';
import type { AlgoliaProduct } from '@jane/search/types';
import { findSpecialForProduct } from '@jane/shared-ecomm/components';
import { LegacyMenuProductCard } from '@jane/shared/components';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, StoreSpecial } from '@jane/shared/models';

import { ListContainer } from './ListContainer';
import {
  EmbedCodeButton,
  HeaderEditButton,
  HeaderToggle,
  HeaderWithoutToggle,
} from './ListItemHeaders';
import { CARD_HEIGHT, CARD_WIDTH } from './utils';

interface Props {
  allStoreSpecials?: StoreSpecial[];
  embeddedMenuRow?: EmbeddedMenuRow | null;
  openEmbedRowModal?: (menuRow: MenuRow, row?: EmbeddedMenuRow | null) => void;
  openMenuRowModal: (row: MenuRow, hasEmbeddedRow?: boolean) => void;
  products?: SmartSortProduct<AlgoliaProduct>[];
  row: MenuRow;
  rowLabel: string;
  showEmbedButton?: boolean;
}
export const ListItem = ({
  allStoreSpecials,
  embeddedMenuRow,
  openEmbedRowModal,
  openMenuRowModal,
  products,
  row,
  rowLabel,
  showEmbedButton,
}: Props) => {
  const c1BundleTag = useFlag(FLAGS.c1BundleTag);
  const rowTypeValue = row.menu_row_type;
  const track = useDebouncedTrack(500);
  const hasSdkProducts = products !== undefined;

  const results = useMemo(() => {
    if (hasSdkProducts) {
      return products.map((p) => p.attributes);
    }

    const items = row?.menu_products_to_preview || [];

    if (rowTypeValue === 'best_selling') {
      items.sort(
        (a, b) => (a.best_seller_rank || 0) - (b.best_seller_rank || 0)
      );
    }

    return items;
  }, [row, rowTypeValue, products, hasSdkProducts]);

  const totalResults = row?.menu_products_ids?.in_stock.length || 0;

  const showEditButton = useMemo(() => !row.is_category, [row]);
  const showToggleHeader = useMemo(() => !row.is_category, [row]);
  const showProductsCTA = useMemo((): boolean => {
    if (!rowTypeValue) return false;
    return (
      !JANE_DEFINED_ROWS.includes(rowTypeValue) &&
      !results.length &&
      !row.applicability_rules?.length
    );
  }, [results, row, rowTypeValue]);
  const showMenuProducts = useMemo(
    () => row.is_category || (!row.is_category && row.enabled),
    [row]
  );
  const categoryRowWithInventory = useMemo(
    () => !!row.is_category && totalResults > 0,
    [row, totalResults]
  );

  return (
    <ListContainer rowEnabled={categoryRowWithInventory || !!row.enabled}>
      <CardCarousel
        afterHeader={
          (showEditButton || (showEmbedButton && openEmbedRowModal)) && (
            <Flex alignItems="center">
              {showEmbedButton && openEmbedRowModal && (
                <EmbedCodeButton
                  hasEmbedCode={!!embeddedMenuRow}
                  onClick={() => openEmbedRowModal(row, embeddedMenuRow)}
                />
              )}
              {showEditButton && (
                <HeaderEditButton
                  onOpenMenuRow={() => openMenuRowModal(row, !!embeddedMenuRow)}
                  showCTAButton={showProductsCTA}
                />
              )}
            </Flex>
          )
        }
        beforeHeader={
          showToggleHeader ? (
            <HeaderToggle
              hasEmbeddedMenuRow={!!embeddedMenuRow}
              row={row}
              rowLabel={rowLabel}
              itemsLength={totalResults}
              showProductsCTA={showProductsCTA}
            />
          ) : (
            <HeaderWithoutToggle
              rowLabel={rowLabel}
              itemsLength={totalResults}
              row={row}
            />
          )
        }
        name={rowLabel}
        mode="ideal-width"
        cardWidth={CARD_WIDTH}
        cardHeight={CARD_HEIGHT}
        cardWidthMobile={212}
        cardHeightMobile={328}
        mb={showMenuProducts && categoryRowWithInventory ? 40 : 0}
        onScroll={(direction: string) => {
          track({
            event: EventNames.HorizontalScroll,
            trigger_source_id: `${direction} arrow`,
          });
        }}
      >
        {!!results.length &&
          showMenuProducts &&
          results.map((product: any) => {
            return (
              <CardCarousel.Card
                key={`${rowTypeValue}-${product.id}-${product.product_id}`}
              >
                <LegacyMenuProductCard
                  appMode="embedded"
                  appliedWeightFilter=""
                  currentSpecial={findSpecialForProduct(
                    product,
                    allStoreSpecials,
                    c1BundleTag
                  )}
                  cartIsOpen={false}
                  cartProducts={[]}
                  disableInteraction={true}
                  disabledState={!row.is_category && !row.enabled}
                  hideActions={true}
                  listView={false}
                  menuRowRules={row.rules}
                  product={product as MenuProduct}
                  store={{} as any}
                  userLocation={{ cityState: 'CA' } as any}
                />
              </CardCarousel.Card>
            );
          })}
      </CardCarousel>
    </ListContainer>
  );
};
